import { useQueries, useQuery } from '@tanstack/react-query';
import { qk, type Period, type Property } from '@immoaktas/api-client';
import { api, role } from '../lib/api';
import { can, today } from '../lib/hooks';
import { actionablePeriod, gapsOf, gapsQuery, periodBadge } from '../routes/landlord/parts/periodGaps';
import type { PillTone } from '../ui';

const BADGE_STALE = 60_000;

/** Counts behind the sidebar badges (same query keys as the screens, so caches are shared); per property when scoped. */
export function useNavCounts(propertyId: string | null) {
  const money = can(role(), 'money');
  const docs = useQuery({
    queryKey: qk.documents({ status: 'needs_review,ready' }),
    queryFn: () => api.documents.list({ status: 'needs_review,ready' }),
    staleTime: BADGE_STALE,
  });
  const readings = useQuery({
    queryKey: qk.readings({ status: 'proposed' }),
    queryFn: () => api.meters.readings({ status: 'proposed' }),
    staleTime: BADGE_STALE,
  });
  const pay = useQuery({
    queryKey: qk.payments(),
    queryFn: () => api.money.overview(),
    staleTime: BADGE_STALE,
    enabled: money,
  });
  const tens = useQuery({
    queryKey: qk.tenancies({}),
    queryFn: () => api.tenancies.list({}),
    staleTime: BADGE_STALE,
    enabled: money && !!propertyId,
  });
  const unread = useQuery({
    queryKey: [...qk.notifications, 'unread'],
    queryFn: () => api.comms.notifications(true),
    staleTime: BADGE_STALE,
  });
  const deadlines = useQuery({ queryKey: qk.deadlines, queryFn: api.money.deadlines, staleTime: 300_000 });
  const inProp = <T extends { propertyId?: string | null }>(list: T[]) =>
    propertyId ? list.filter((x) => x.propertyId === propertyId) : list;
  const owing = (pay.data?.rows ?? []).filter((x) => x.balanceCents > 0);
  const propOf = new Map((tens.data ?? []).map((x) => [x.id, x.propertyId]));
  return {
    documents: inProp(docs.data?.items ?? []).length,
    readings: inProp(readings.data ?? []).length,
    arrears: propertyId ? owing.filter((x) => propOf.get(x.tenancyId) === propertyId).length : owing.length,
    unread: unread.data?.items.length ?? 0,
    overdue: deadlines.data?.filter((d) => d.severity === 'overdue').length ?? 0,
  };
}

/** What the sidebar says about one property: units, empty units, state of the billing year that needs work. */
/**
 * Vacant units of a property: billable units existing today without an active tenancy (a cellar, a unit not built
 * yet or a parking space someone rents never count). Older servers without `billableUnitIds`: all units.
 */
export function vacantOf(p: Pick<Property, 'unitCount' | 'billableUnitIds'>, rented: ReadonlySet<string>): number {
  if (p.billableUnitIds) return p.billableUnitIds.filter((id) => !rented.has(id)).length;
  return Math.max(0, (p.unitCount ?? 0) - rented.size);
}

export type PropertyFacts = {
  units: number;
  /** units without a running tenancy today; null while loading */
  vacant: number | null;
  /** billing period that needs work now (Dashboard and Objekte use the same choice) */
  period?: Period;
  /** shared readiness sentence or period status, with its tone; null without a period */
  status: { label: string; tone: PillTone } | null;
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Facts of every property for the sidebar cards and the "Du bist im Objekt" card. Uses the query keys of the
 * Dashboard and Objekte screens (active tenancies, periods, year files of collecting periods), so nothing loads twice.
 */
export function usePropertyFacts(t: TFn, properties: Property[]): Map<string, PropertyFacts> {
  const active = useQuery({
    queryKey: qk.tenancies({ active: true }),
    queryFn: () => api.tenancies.list({ active: true }),
    staleTime: BADGE_STALE,
  });
  const periods = useQuery({
    queryKey: qk.periods(),
    queryFn: () => api.billing.periods(),
    staleTime: BADGE_STALE,
  });
  const day = today();
  const current = new Map(properties.map((p) => [p.id, actionablePeriod(periods.data ?? [], p.id, day)]));
  const collecting = [...current.values()].filter((p): p is Period => p?.status === 'collecting');
  const files = useQueries({ queries: collecting.map((p) => ({ ...gapsQuery(p), staleTime: BADGE_STALE })) });
  const rented = new Map<string, Set<string>>();
  for (const x of active.data ?? []) {
    if (!x.propertyId) continue;
    const set = rented.get(x.propertyId) ?? new Set<string>();
    set.add(x.unitId);
    rented.set(x.propertyId, set);
  }
  return new Map(
    properties.map((p) => {
      const period = current.get(p.id);
      const file = period ? files[collecting.indexOf(period)]?.data : undefined;
      const units = p.unitCount ?? 0;
      return [
        p.id,
        {
          units,
          vacant: active.data ? vacantOf(p, rented.get(p.id) ?? new Set()) : null,
          period,
          status: periodBadge(t, period, file ? gapsOf(file) : undefined),
        },
      ];
    }),
  );
}

/** "7 Einheiten · 1 leer" / "1 Einheit · vermietet" / "Noch keine Einheiten". */
export function unitsLine(t: TFn, f: PropertyFacts | undefined): string {
  if (!f || !f.units) return t('shell.objekt.noUnits');
  const units = t('shell.objekt.units', { count: f.units });
  if (f.vacant == null) return units;
  return `${units} · ${f.vacant ? t('shell.objekt.vacant', { count: f.vacant }) : t('shell.objekt.rented', { count: f.units })}`;
}
